import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import { TechRadarApi } from '@backstage-community/plugin-tech-radar';
import { TechRadarLoaderResponse } from '@backstage-community/plugin-tech-radar-common';
import {
  radarDocumentSchema,
  radarIndexSchema,
  RadarIndex,
  RadarNotFoundError,
  RadarUnavailableError,
  toTechRadarLoaderResponse,
} from '@appfolio/backstage-tech-radar';

export interface TechRadarIndexApi {
  getIndex(): Promise<RadarIndex>;
}

export const techRadarIndexApiRef = createApiRef<TechRadarIndexApi>({
  id: 'plugin.tech-radar-index.service',
});

export class TechRadarClient implements TechRadarApi, TechRadarIndexApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  public constructor(deps: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = deps.discoveryApi;
    this.fetchApi = deps.fetchApi;
  }

  public async load(id: string | undefined): Promise<TechRadarLoaderResponse> {
    const radarId = id ?? (await this.getIndex()).defaultRadarId;
    const baseUrl = await this.discoveryApi.getBaseUrl('tech-radar');
    const response = await this.fetchApi.fetch(`${baseUrl}/radars/${radarId}`);
    if (!response.ok) {
      throw await this.toRadarError(radarId, response);
    }
    const document = radarDocumentSchema.parse(await response.json());
    return toTechRadarLoaderResponse(document);
  }

  public async getIndex(): Promise<RadarIndex> {
    const baseUrl = await this.discoveryApi.getBaseUrl('tech-radar');
    const response = await this.fetchApi.fetch(`${baseUrl}/index`);
    if (!response.ok) {
      throw new Error(
        `Could not load the tech radar index: ${response.status}`,
      );
    }
    return radarIndexSchema.parse(await response.json());
  }

  private async toRadarError(
    radarId: string,
    response: Response,
  ): Promise<Error> {
    const body = await response.json().catch(() => undefined);
    if (response.status === 404 && body?.availableRadars) {
      return new RadarNotFoundError(radarId, body.availableRadars);
    }
    if (response.status === 502) {
      return new RadarUnavailableError(radarId, body?.message ?? 'unavailable');
    }
    return new Error(
      body?.message ?? `Request failed with status ${response.status}`,
    );
  }
}
